import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { Button, CircularProgress, Box, Snackbar, Alert } from "@mui/material";
import { useSelector } from "react-redux";
import { Layout } from "../../components/layout";
import PageHeader from "../../components/PageHeader";
import PersonsInfo from "../../components/PersonsInfo";
import PageSection from "../../components/PageSection";
import { sessionsService, Session } from "../../services/sessionsService";
import {
  useModal,
  EditTranscriptModalContent,
  EditDiagnosisModalContent,
  EditNoteModalContent,
  GeneratingPlanModalContent,
} from "../../components/Modal";
import {
  treatmentPlanService,
  TreatmentPlanListItem,
} from "../../services/treatmentPlanService";
import {
  Popover,
  TreatmentPlanSelectPopoverContent,
} from "../../components/Popover";
import { useTranslation } from "react-i18next";
import { EditIcon, RightArrowIcon, BookPlusIcon } from "../../components/Icons";
import { colors } from "../../theme/colors";
import { formatTranscript } from "../../utils";
import { selectIsProvider } from "../../store/selectors/authSelectors";

interface GeneratedPlanNotification {
  id: number;
  name: string;
}

const SessionDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Treatment plans state
  const [viewPlanAnchorEl, setViewPlanAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlanListItem[]>(
    [],
  );
  const [plansLoading, setPlansLoading] = useState(false);
  const [generatedPlanNotification, setGeneratedPlanNotification] =
    useState<GeneratedPlanNotification | null>(null);
  const [generateError, setGenerateError] = useState(false);
  const sessionFetchedRef = useRef(false);
  const treatmentPlansFetchedRef = useRef(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isViewPlanPopoverOpen = Boolean(viewPlanAnchorEl);

  const isProvider = useSelector(selectIsProvider);

  const fetchSessionDetails = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const sessionData = await sessionsService.getSessionById(Number(id));
      setSession(sessionData);
    } catch (err) {
      setError(t("sessionDetails.failedToLoad"));
      console.error("Error fetching session details:", err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (sessionFetchedRef.current) return;

    sessionFetchedRef.current = true;
    fetchSessionDetails();
  }, [fetchSessionDetails]);

  // Fetch treatment plans for this session
  useEffect(() => {
    if (!id || treatmentPlansFetchedRef.current) return;

    treatmentPlansFetchedRef.current = true;

    treatmentPlanService
      .getList({ sessionId: Number(id) })
      .then((response) => {
        setTreatmentPlans(response.data);
      })
      .catch((err) => {
        console.error("Error fetching treatment plans:", err);
        setTreatmentPlans([]);
      });
  }, [id]);

  // Handle success notification from URL params after page reload
  useEffect(() => {
    const planId = searchParams.get("generatedPlanId");
    const planName = searchParams.get("generatedPlanName");

    if (planId && planName) {
      setGeneratedPlanNotification({
        id: Number(planId),
        name: decodeURIComponent(planName),
      });
      // Clean up URL params
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Save session data to API
  const saveSessionData = useCallback(
    async (data: {
      transcript?: string;
      preliminaryDiagnosis?: string;
      notes?: string;
    }) => {
      if (!session) return;

      try {
        await sessionsService.saveSession(session.id, {
          transcript: data.transcript ?? session.transcript ?? "",
          preliminaryDiagnosis:
            data.preliminaryDiagnosis ?? session.preliminaryDiagnosis ?? "",
          notes: data.notes ?? session.notes ?? "",
          status: session.status,
        });

        // Update local state after successful save
        setSession((prev) => (prev ? { ...prev, ...data } : prev));
      } catch (err) {
        console.error("Error saving session:", err);
        throw err;
      }
    },
    [session],
  );

  const handleOpenTranscriptModal = () => {
    if (!session) return;

    openModal({
      id: "edit-transcript-modal",
      title: "",
      content: (
        <EditTranscriptModalContent
          transcript={session.transcript || ""}
          onSave={async (newTranscript) => {
            await saveSessionData({ transcript: newTranscript });
          }}
          onClose={() => closeModal("edit-transcript-modal")}
        />
      ),
      showCloseButton: false,
      maxWidth: "1200px",
      containerPadding: "60px",
      contentPadding: "0",
      closeOnBackdropClick: false,
    });
  };

  const handleOpenDiagnosisModal = () => {
    if (!session) return;

    openModal({
      id: "edit-diagnosis-modal",
      title: "",
      content: (
        <EditDiagnosisModalContent
          diagnosis={session.preliminaryDiagnosis || ""}
          onSave={async (newDiagnosis) => {
            await saveSessionData({ preliminaryDiagnosis: newDiagnosis });
          }}
          onClose={() => closeModal("edit-diagnosis-modal")}
        />
      ),
      showCloseButton: false,
      maxWidth: "500px",
      containerPadding: "40px",
      contentPadding: "0",
      closeOnBackdropClick: false,
    });
  };

  const handleOpenNoteModal = () => {
    if (!session) return;

    openModal({
      id: "edit-note-modal",
      title: "",
      content: (
        <EditNoteModalContent
          note={session.notes || ""}
          onSave={async (newNote) => {
            await saveSessionData({ notes: newNote });
          }}
          onClose={() => closeModal("edit-note-modal")}
        />
      ),
      showCloseButton: false,
      maxWidth: "500px",
      containerPadding: "40px",
      contentPadding: "0",
      closeOnBackdropClick: false,
    });
  };

  const handleGeneratePlan = async () => {
    if (!id) return;

    openModal({
      id: "generating-plan",
      content: <GeneratingPlanModalContent />,
      showCloseButton: false,
      closeOnBackdropClick: false,
    });

    try {
      const { treatmentPlanId } = await treatmentPlanService.generate(
        Number(id),
      );

      // Start polling to check if treatment plan is ready (every 3 seconds)
      // Status -1 = GeneratingTriggered, 0 = Generating - keep waiting
      // Status 1 = AIDrafted - generation complete, redirect
      // Status 8 = GenerationFailed - show error
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const plan = await treatmentPlanService.getById(treatmentPlanId);

          // Check if generation failed (status 8)
          if (plan.status === 8) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            closeModal("generating-plan");
            setGenerateError(true);
            return;
          }

          // Check if plan is ready (status >= 1, excluding error status 8)
          // Status -1 and 0 mean still generating
          if (plan.status >= 1) {
            // Plan is ready - stop polling, close modal, and redirect to treatment plan
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            closeModal("generating-plan");

            // Redirect to the generated treatment plan page
            navigate(`/treatment-plan/${plan.id}`);
          }
          // If status is -1 or 0, continue polling
        } catch {
          // Error fetching plan - continue polling
        }
      }, 3000);
    } catch (err) {
      console.error("Error generating treatment plan:", err);
      closeModal("generating-plan");
      setGenerateError(true);
    }
  };

  const handleViewPlanClick = async (event: React.MouseEvent<HTMLElement>) => {
    if (!id) return;

    setViewPlanAnchorEl(event.currentTarget);
    setPlansLoading(true);

    try {
      const response = await treatmentPlanService.getList({
        sessionId: Number(id),
      });
      setTreatmentPlans(response.data);
    } catch (err) {
      console.error("Error fetching treatment plans:", err);
    } finally {
      setPlansLoading(false);
    }
  };

  const handleViewPlanClose = () => {
    setViewPlanAnchorEl(null);
  };

  const handleSelectPlan = (planId: number) => {
    setViewPlanAnchorEl(null);
    navigate(`/treatment-plan/${planId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Format duration from "HH:MM:SS.ms" to "MM:SS"
  const formatDuration = (duration: string) => {
    if (!duration) return "00:00";
    const parts = duration.split(":");
    if (parts.length === 3) {
      // HH:MM:SS.ms format - return MM:SS (without milliseconds)
      const seconds = parts[2].split(".")[0]; // Remove milliseconds
      return `${parts[1]}:${seconds}`;
    }
    return duration;
  };

  const getSessionTypeName = (type: number) => {
    return type === 1
      ? t("sessionDetails.consultancy")
      : t("sessionDetails.treatment");
  };

  if (loading) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      </Layout>
    );
  }

  if (error || !session) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            gap: 2,
          }}
        >
          <div>{error || t("sessionDetails.sessionNotFound")}</div>
          <button onClick={fetchSessionDetails}>
            {t("sessionDetails.retry")}
          </button>
        </Box>
      </Layout>
    );
  }

  const isConsultancy = session.type === 1;

  // Prepare meta items for header
  const metaItems = [
    { text: formatDate(session.startedAt), key: "date" },
    { text: formatTime(session.startedAt), key: "time" },
    { text: formatDuration(session.duration || ""), key: "duration" },
  ];

  // Form provider full name from firstName and lastName
  const providerFullName = [session.providerFirstName, session.providerLastName]
    .filter(Boolean)
    .join(" ");

  // Form patient full name from firstName and lastName
  const patientFullName = [session.patientFirstName, session.patientLastName]
    .filter(Boolean)
    .join(" ");

  const persons = [
    {
      name: patientFullName || `Patient ${session.patientUid}`,
      role: t("sessionDetails.patient"),
    },
    {
      name: providerFullName || `Provider ${session.accountId}`,
      role: t("sessionDetails.initiatedBy"),
      avatarUrl: session?.providerAvatarUrl,
    },
    ...(session.type === 2
      ? [
          {
            name:
              session.treatmentPlan?.name ||
              t("sessionDetails.noPlanAvailable"),
            role: t("sessionDetails.treatmentPlan"),
            customIcon: <BookPlusIcon width="24px" height="24px" />,
            iconBackgroundColor: colors.background.mediumBlue,
            ...(session.treatmentPlan
              ? { linkTo: `/treatment-plan/${session.treatmentPlan.id}` }
              : {}),
          },
        ]
      : []),
  ];

  return (
    <Layout>
      <PageHeader
        title={`${getSessionTypeName(session.type)} ${t(
          "sessionDetails.sessionDetails",
        )}`}
        metaItems={metaItems}
        actions={
          isConsultancy ? (
            <>
              {treatmentPlans.length > 0 && (
                <>
                  <Button variant="outlined" onClick={handleViewPlanClick}>
                    {t("sessionDetails.viewTreatmentPlan")}
                    <RightArrowIcon
                      width="24px"
                      height="24px"
                      fill={colors.primary.main}
                    />
                  </Button>
                  <Popover
                    open={isViewPlanPopoverOpen}
                    anchorEl={viewPlanAnchorEl}
                    onClose={handleViewPlanClose}
                    position="bottom"
                  >
                    <TreatmentPlanSelectPopoverContent
                      plans={treatmentPlans}
                      loading={plansLoading}
                      onSelect={handleSelectPlan}
                    />
                  </Popover>
                </>
              )}
              {isProvider && (
                <Button variant="contained" onClick={handleGeneratePlan}>
                  {t("sessionDetails.generateTreatmentPlan")}
                  <RightArrowIcon width="24px" height="24px" />
                </Button>
              )}
            </>
          ) : undefined
        }
      />

      <PersonsInfo persons={persons} avatarSize={50} />

      <PageSection
        title={t("sessionDetails.transcriptTitle")}
        content={formatTranscript(session.transcript || "")}
        maxContentHeight="120px"
        actions={
          isProvider ? (
            <Button
              size="small"
              variant="outlined"
              onClick={handleOpenTranscriptModal}
            >
              {t("sessionDetails.readFullTranscript")}
            </Button>
          ) : undefined
        }
      />

      {isConsultancy && (
        <PageSection
          title={t("sessionDetails.preliminaryDiagnosis")}
          content={
            session.preliminaryDiagnosis || t("sessionDetails.noDiagnosis")
          }
          actions={
            isProvider ? (
              <Button
                size="small"
                variant="outlined"
                onClick={handleOpenDiagnosisModal}
              >
                <EditIcon width="16px" height="16px" />
                {t("sessionDetails.editDiagnosis")}
              </Button>
            ) : undefined
          }
        />
      )}

      <PageSection
        title={t("sessionDetails.noteFromProvider")}
        content={session.notes || t("sessionDetails.noNote")}
        actions={
          isProvider ? (
            <Button
              size="small"
              variant="outlined"
              onClick={handleOpenNoteModal}
            >
              <EditIcon width="16px" height="16px" />
              {t("sessionDetails.editNote")}
            </Button>
          ) : undefined
        }
      />

      {/* Success notification for generated treatment plan */}
      <Snackbar
        open={!!generatedPlanNotification}
        autoHideDuration={10000}
        onClose={() => setGeneratedPlanNotification(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setGeneratedPlanNotification(null)}
          severity="success"
          sx={{ width: "100%" }}
        >
          <Box>
            {t("sessionDetails.treatmentPlanGenerated")}:{" "}
            <Link
              to={`/treatment-plan/${generatedPlanNotification?.id}`}
              style={{ color: "inherit", fontWeight: 600 }}
            >
              {generatedPlanNotification?.name}
            </Link>
          </Box>
        </Alert>
      </Snackbar>

      {/* Error notification for failed generation */}
      <Snackbar
        open={generateError}
        autoHideDuration={6000}
        onClose={() => setGenerateError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setGenerateError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {t("sessionDetails.treatmentPlanGenerationFailed")}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default SessionDetailsPage;
